import React, { useState, useRef, useEffect } from 'react';
import { Camera, MapPin, AlertTriangle, X, User, Move } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

interface Location {
  latitude: number;
  longitude: number;
  accuracy: number;
}

interface RegisteredFace {
  name: string;
  image: string;
  timestamp: string;
}

interface SelectionRect {
  startX: number;
  startY: number;
  width: number;
  height: number;
}

const EdgeCamera = () => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string>('Initializing...');
  const [isCapturing, setIsCapturing] = useState(false);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [registerName, setRegisterName] = useState('');
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registeredFaces, setRegisteredFaces] = useState<RegisteredFace[]>([]);
  const [selectedImage, setSelectedImage] = useState<RegisteredFace | null>(null);
  const [snapshotImage, setSnapshotImage] = useState<string | null>(null);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const captureInterval = useRef<NodeJS.Timer | null>(null);
  const selectionRef = useRef<HTMLDivElement>(null);
  const startPosRef = useRef<{x: number, y: number} | null>(null);
  
  
  // Initialize or get device ID
  useEffect(() => {
    const storedId = localStorage.getItem('deviceId');
    if (storedId) {
      setDeviceId(storedId);
    } else {
      const newId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
      localStorage.setItem('deviceId', newId);
      setDeviceId(newId);
    }
  }, []);

  // Initialize camera and location
  const initializeServices = async () => {
    try {
      // Request camera access
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      
      // Try to get location, but don't require it
      if (navigator.geolocation) {
        navigator.geolocation.watchPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy
            });
          },
          (err) => {
            console.warn('Location not available:', err);
            // TODO: we don't need to error here but we can 
          }
        );
      }
      
      setStatus('Camera initialized');
    } catch (err) {
      const error = err as Error;
      setError(`Failed to initialize camera: ${error.message}`);
    }
  };

  // Capture and send image
  const captureImage = async (isRegistration = false) => {
    if (!videoRef.current) return;

    const canvas = document.createElement('canvas');
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.drawImage(video, 0, 0);
    const imageData = canvas.toDataURL('image/jpeg', 0.8);

    if (isRegistration) {
      setSnapshotImage(imageData);
      setShowRegisterModal(true);
      setSelection(null);
    } else {
      try {
        const response = await fetch('https://democluster.augurinitiative.ai/report', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            cameraId: deviceId,
            timestamp: new Date().toISOString(),
            location: location || null,
            image: imageData
          })
        });
        if (response.ok) {
          setStatus(`Captured at ${new Date().toLocaleTimeString()}`);
        } else {
          setError('Failed to send image');
        }
      } catch (err) {
        const error = err as Error;
        setError(`Capture error: ${error.message}`);
      }
    }
  };

  const registerFace = async (imageData: string) => {
    try {
      const response = await fetch('https://democluster.augurinitiative.ai/register-face', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerName,
          image: imageData
        })
      });
      
      if (response.ok) {
        setRegisteredFaces(prev => [...prev, {
          name: registerName,
          image: imageData,
          timestamp: new Date().toISOString()
        }]);
        setStatus('Face registered successfully');
        setShowRegisterModal(false);
        setSnapshotImage(null);
      } else {
        setError('Failed to register face');
      }
    } catch (err) {
      const error = err as Error;
      setError(`Registration error: ${error.message}`);
    }
  };

  // Toggle continuous capture
  const toggleCapture = () => {
    if (isCapturing) {
      if (captureInterval.current) {
        clearInterval(captureInterval.current);
        captureInterval.current = null;
      }
      setIsCapturing(false);
      setStatus('Capture stopped');
    } else {
      captureInterval.current = setInterval(() => captureImage(false), 200);
      setIsCapturing(true);
      setStatus('Capturing...');
    }
  };

  useEffect(() => {
    initializeServices();
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (captureInterval.current) {
        clearInterval(captureInterval.current);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Status Card */}
        <Card className="bg-black/80 backdrop-blur-sm border-gray-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Edge Camera</h2>
              <div className="flex items-center space-x-2">
                {location && (
                  <div className="flex items-center text-sm text-gray-400">
                    <MapPin size={16} className="mr-1" />
                    {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  </div>
                )}
                <div className={`w-2 h-2 rounded-full ${isCapturing ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Camera Feed */}
        <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-900">
          <video 
            ref={videoRef}
            autoPlay 
            playsInline
            className="w-full h-full object-cover"
          />
        </div>

        {/* Controls */}
        <div className="flex gap-4">
          <button
            onClick={toggleCapture}
            className={`flex-1 px-4 py-2 rounded-md transition-colors ${
              isCapturing 
                ? 'bg-red-500 hover:bg-red-600' 
                : 'bg-orange-500 hover:bg-orange-600'
            }`}
          >
            {isCapturing ? 'Stop Capture' : 'Start Capture'}
          </button>
          <button
            onClick={() => captureImage(true)}
            className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 rounded-md transition-colors"
          >
            Register Face
          </button>
        </div>

        {/* Registered Faces */}
        {registeredFaces.length > 0 && (
        <Card className="bg-black/80 backdrop-blur-sm border-gray-800">
            <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-4">Registered Faces</h3>
            <div className="grid grid-cols-2 gap-4">
                {registeredFaces.map((face, index) => (
                <div key={index} className="bg-gray-800/50 rounded-lg p-3">
                    <div className="flex items-center space-x-2 mb-2">
                    <User size={16} className="text-green-400" />
                    <span className="text-green-400">{face.name}</span>
                    </div>
                    <div 
                    className="cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedImage(face)}
                    >
                    <img 
                        src={face.image} 
                        alt={face.name}
                        className="w-full aspect-video object-cover rounded-md"
                    />
                    </div>
                    <div className="text-xs text-gray-400 mt-2">
                    {new Date(face.timestamp).toLocaleString()}
                    </div>
                </div>
                ))}
            </div>
            </CardContent>
        </Card>
        )}

        {/* Full Image Modal */}
        {selectedImage && (
        <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedImage(null)}
        >
            <div className="relative max-w-4xl w-full">
            <button 
                onClick={() => setSelectedImage(null)}
                className="absolute -top-12 right-0 text-gray-400 hover:text-white"
            >
                <X size={24} />
            </button>
            <div className="bg-black/60 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-4">
                <User size={20} className="text-green-400" />
                <span className="text-green-400 text-lg">{selectedImage.name}</span>
                <span className="text-gray-400 text-sm ml-auto">
                    {new Date(selectedImage.timestamp).toLocaleString()}
                </span>
                </div>
                <img 
                src={selectedImage.image} 
                alt={selectedImage.name}
                className="w-full rounded-lg"
                />
            </div>
            </div>
        </div>
        )}

        {/* Status/Error Messages */}
        {(status || error) && (
          <Card className="bg-black/80 backdrop-blur-sm border-gray-800">
            <CardContent className="p-4">
              {error ? (
                <div className="flex items-center text-red-400">
                  <AlertTriangle size={16} className="mr-2" />
                  {error}
                </div>
              ) : (
                <div className="text-gray-400">{status}</div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Face Registration Modal */}
        {showRegisterModal && snapshotImage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
            <Card className="w-full max-w-2xl bg-black/90 border-gray-800">
            <CardContent className="p-6">
                <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Register New Face</h3>
                <button 
                    onClick={() => {
                    setShowRegisterModal(false);
                    setSnapshotImage(null);
                    }}
                    className="text-gray-400 hover:text-white"
                >
                    <X size={20} />
                </button>
                </div>

                <div className="mb-4">
                <img 
                    src={snapshotImage} 
                    alt="Snapshot" 
                    className="w-full rounded-lg"
                />
                </div>

                <input
                type="text"
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                placeholder="Enter name"
                className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 mb-4"
                />

                <button
                onClick={() => registerFace(snapshotImage)}
                disabled={!registerName}
                className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                Register Face
                </button>
            </CardContent>
            </Card>
        </div>
        )}
      </div>
    </div>
  );
};

export default EdgeCamera;